import CourseCard, { type Course } from '@/components/CourseCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { router, usePage } from '@inertiajs/react';
import { GraduationCap } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ContinueLearning from '../Home/Partials/ContinueLearning';

interface Category {
    id: number;
    name: string;
    courses: Course[];
}

interface Props {
    categories_with_courses?: Category[];
}

const IndexContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { categories_with_courses = [] } = props;

    const totalCourses = categories_with_courses.reduce(
        (sum, category) => sum + category.courses.length,
        0,
    );

    const handleCourseClick = (course: Course) => {
        router.visit(`/courses/${course.slug}/`);
    };

    return (
        <AppLayout>
            <PageTitle title={t('home.courses')} />
            <div className="mx-auto max-w-6xl space-y-6">
                {/* Continue where you left off */}
                <ContinueLearning />

                {totalCourses === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <GraduationCap className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                {t('home.noCoursesEnrolled')}
                            </h3>
                            <p className="text-gray-600">
                                {t('home.exploreCatalog')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-semibold text-gray-900">
                                {totalCourses}{' '}
                                {t('myAccount.coursesCount', { count: totalCourses })}
                            </h2>
                        </div>

                        {categories_with_courses.map((category) => (
                            <div key={category.id} className="space-y-4">
                                <h3 className="text-xl font-semibold text-gray-900">
                                    {category.name}
                                </h3>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                    {category.courses.map((course) => (
                                        <CourseCard
                                            key={course.id}
                                            course={course}
                                            onClick={() => handleCourseClick(course)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </AppLayout>
    );
};

const Index: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <IndexContent />
        </ThemeWrapper>
    );
};

export default Index;
